import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom"; // ✅ Add useSearchParams
import QuantityControl from "../helper/QuantityControl";
import { getCustomerToken } from "../utils/CustomerAuth";
import { CartServices } from "../services/CartServices";

const API_BASE = "https://api.ifjona.com";

const toNumber = (val) => { const n = Number(String(val ?? "").replace(/[^\d.]/g, "")); return Number.isFinite(n) ? n : 0; };

const getCurrencySymbol = (item) => {
  if (item?.resolved_price?.currency_symbol) {
    return item.resolved_price.currency_symbol;
  }
  if (item?.location?.currency?.symbol) {
    return item.location.currency.symbol;
  }
  if (item?.locationProductPrice?.location?.currency?.symbol) {
    return item.locationProductPrice.location.currency.symbol;
  }
  if (item?.productInfoLocationPrice?.location?.currency?.symbol) {
    return item.productInfoLocationPrice.location.currency.symbol;
  }
  return "$";
};

const money = (amount, currencySymbol = "$") => `${currencySymbol}${Number(amount || 0).toFixed(2)}`;

const toImageUrl = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_BASE}${s}`;
  if (s.startsWith("storage/"))  return `${API_BASE}/${s}`;
  return `${API_BASE}/storage/${s.replace(/^\/+/, "").replace(/^storage\//, "")}`;
};

const resolveImage     = (item) => toImageUrl((item?.product_info || item?.productInfo)?.image || item?.product?.image) || "/assets/images/thumbs/product-thumb-1.png";
const resolveTitle     = (item) => item?.product?.title || "Product";
const resolveDetailsUrl= (item) => item?.product?.id ? `/product-details/${item.product.id}` : "/product-details";
const resolveCategory  = (item) => item?.product?.category?.name || item?.product?.category_name || "";

const resolveUnitPrice = (item) => {
  if (item?.resolved_price?.unit_price) {
    return toNumber(item.resolved_price.unit_price);
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  if (item?.locationProductPrice?.flash_sales_price != null && item.locationProductPrice.flash_sales_price !== "") {
    return toNumber(item.locationProductPrice.flash_sales_price);
  }

  if (productInfo?.flash_sale_price != null && productInfo.flash_sale_price !== "") {
    return toNumber(productInfo.flash_sale_price);
  }

  if (product?.flash_sale_price != null && product.flash_sale_price !== "") {
    return toNumber(product.flash_sale_price);
  }

  const override = productInfo?.current_price_override ?? null;
  if (override !== null && override !== "") {
    return toNumber(override);
  }

  if (item?.locationProductPrice?.base_current_price != null) {
    return toNumber(item.locationProductPrice.base_current_price);
  }

  return toNumber(product?.base_current_price ?? 0);
};

const hasFlashSale = (item) => {
  if (item?.resolved_price?.is_flash_sale) return true;

  if (item?.locationProductPrice?.flash_sales_price != null && item.locationProductPrice.flash_sales_price !== "") {
    return true;
  }

  const productInfo = item?.product_info || item?.productInfo;
  const product = item?.product;

  if (productInfo?.flash_sale_price != null && productInfo.flash_sale_price !== "") return true;
  if (product?.flash_sale_price != null && product.flash_sale_price !== "") return true;

  return false;
};



const CSS = `
  :root {
    --g-primary: #299e60;
    --g-light:   #f3faf2;
    --g-border:  #c4e8d1;
    --g-hover:   #228a52;
    --g-text:    #1a5c3a;
    --flash-red: #dc2626;
    --flash-bg:  #fef2f2;
  }

  .cs-cb        { accent-color: var(--g-primary); width:17px; height:17px; cursor:pointer; flex-shrink:0; }
  .cs-sel-row   { background: var(--g-light) !important; }
  .cs-ptitle    { font-size:13.5px; font-weight:700; color:#1d2939; text-decoration:none; line-height:1.4; }
  .cs-ptitle:hover { color: var(--g-primary); }
  .cs-cat       { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.6px; }
  .cs-xbtn      { background:none; border:none; cursor:pointer; color:#d0d5dd; padding:5px; border-radius:6px; display:flex; align-items:center; transition:color .15s,background .15s; }
  .cs-xbtn:hover { color:#d94040; background:#fff5f5; }
  .cs-hint      { background: var(--g-light); border:1px dashed var(--g-border); border-radius:8px; padding:9px 14px; font-size:12px; color: var(--g-text); font-weight:600; text-align:center; margin:0 0 12px; }

  .cs-flash-badge { 
    display: inline-flex; 
    align-items: center; 
    gap: 4px;
    background: var(--flash-bg); 
    color: var(--flash-red); 
    font-size: 10px; 
    font-weight: 700; 
    padding: 2px 8px; 
    border-radius: 12px; 
    margin-left: 6px;
  }

  .cs-toolbar   { background: var(--g-light); border:1px solid var(--g-border); border-radius:8px; padding:10px 16px; margin-bottom:16px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; }
  .cs-sel-badge { background:#fff; color: var(--g-primary); border:1px solid var(--g-border); border-radius:20px; font-size:11px; font-weight:700; padding:2px 10px; }
  .cs-rem-sel   { background:#fff5f5; border:1px solid #fcc; border-radius:6px; padding:5px 12px; font-size:12px; font-weight:700; color:#d94040; cursor:pointer; }
  .cs-rem-sel:hover { background:#ffe0e0; }

  .cs-thumb     { width:72px; height:72px; object-fit:cover; border-radius:8px; }
  .cs-thead th  { font-size:11px; letter-spacing:.8px; text-transform:uppercase; color:#98a2b3; font-weight:800; padding:12px 10px; background:#f9f9f9; border-bottom:2px solid #ececec; }
  .cs-trow td   { padding:14px 10px; border-bottom:1px solid #f5f5f5; vertical-align:middle; }
  .cs-trow:last-child td { border-bottom:none; }
  .cs-trow.cs-sel-row td { background: var(--g-light); }
  .cs-price     { font-size:14px; font-weight:700; color:#475467; white-space:nowrap; }
  .cs-price.cs-flash { color: var(--flash-red); }
  .cs-sub       { font-size:14px; font-weight:800; color: var(--g-primary); white-space:nowrap; }
  .cs-sub.cs-flash { color: var(--flash-red); }

  .cs-mcard { border:1px solid #ececec; border-radius:12px; margin-bottom:12px; overflow:hidden; transition:border-color .15s; }
  .cs-mcard.cs-sel-row { background: var(--g-light); border-color: var(--g-border); }

  .cs-m-top { display:flex; align-items:flex-start; gap:12px; padding:14px 14px 10px; }
  .cs-mimg  { width:80px; height:80px; object-fit:cover; border-radius:10px; border:1px solid #ececec; flex-shrink:0; }
  .cs-m-info { flex:1; min-width:0; }
  .cs-m-title-row { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; margin-bottom:4px; }

  .cs-m-bottom { display:flex; align-items:center; justify-content:space-between; padding:10px 14px 12px; border-top:1px solid #f5f5f5; background: var(--g-light); gap:8px; flex-wrap:wrap; }
  .cs-m-pricebox { display:flex; flex-direction:column; gap:2px; }
  .cs-m-plabel   { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.5px; }
  .cs-m-pval     { font-size:13px; font-weight:700; color:#475467; }
  .cs-m-pval.cs-flash { color: var(--flash-red); }
  .cs-m-subbox   { display:flex; flex-direction:column; align-items:flex-end; gap:2px; }
  .cs-m-sublabel { font-size:10px; font-weight:700; color:#b0b8c8; text-transform:uppercase; letter-spacing:.5px; }
  .cs-m-subval   { font-size:15px; font-weight:800; color: var(--g-primary); }
  .cs-m-subval.cs-flash { color: var(--flash-red); }
`;

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const CartSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // ✅ Add this
  const [cartItems, setCartItems]     = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [coupon, setCoupon]           = useState("");
  const qtyTimers = useRef({});

  useEffect(() => {
    if (!getCustomerToken()) navigate("/login", { state: { from: "/cart" } });
  }, [navigate]);

  // ✅ FIXED: Add location filter to dependency array
  const locationFilter = useMemo(() => {
    return (searchParams.get("loc") || "").trim();
  }, [searchParams]);

  

  const visibleCartItems = useMemo(() => {
    if (!locationFilter) return cartItems;

    return cartItems.filter((item) => {
      const itemLocationName = String(item?.location?.name || "").trim().toLowerCase();
      return itemLocationName === locationFilter.toLowerCase();
    });
  }, [cartItems, locationFilter]);

  const loadCart = useCallback(async () => {
    try {
      const items = await CartServices.getCart();
      const arr = Array.isArray(items) ? items : [];
      setCartItems(arr);
      setSelectedIds(prev => prev.size === 0 && arr.length > 0 ? new Set(arr.map(i => String(i.id))) : prev);
    } catch (err) {
      console.error("Failed to load cart:", err);
      if ([401, 403].includes(err?.response?.status)) navigate("/login", { state: { from: "/cart" } });
    }
  }, []); // ✅ Remove locationFilter from here - we handle it via effect below

  // ✅ Initial load
  useEffect(() => {
    loadCart();
    const evts = ["cart_updated", "customer_logged_in", "customer_logged_out", "customer_auth_changed"];
    evts.forEach(e => window.addEventListener(e, loadCart));
    return () => {
      evts.forEach(e => window.removeEventListener(e, loadCart));
      Object.values(qtyTimers.current).forEach(t => clearTimeout(t));
    };
  }, [loadCart]);

  // ✅ NEW: Reload cart when location changes
  useEffect(() => {
    loadCart();
  }, [locationFilter, loadCart]);

  const removeItem = async (id) => {
    try {
      await CartServices.remove(id);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });
      await loadCart();
      window.dispatchEvent(new Event("cart_updated"));
    } catch (err) { console.error("Remove failed:", err); }
  };

  const removeSelected = async () => {
    for (const id of selectedVisibleIds) {
      try { await CartServices.remove(id); } catch {}
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectedVisibleIds.forEach((id) => next.delete(String(id)));
      return next;
    });

    await loadCart();
    window.dispatchEvent(new Event("cart_updated"));
  };

  const setQty = useCallback((id, qty) => {
    const q = Math.max(1, Number(qty || 1));
    setCartItems(prev => prev.map(x => String(x?.id) === String(id) ? { ...x, quantity: q } : x));
    clearTimeout(qtyTimers.current[id]);
    qtyTimers.current[id] = setTimeout(async () => {
      try { await CartServices.update(id, q); window.dispatchEvent(new Event("cart_updated")); }
      catch (err) { console.error("Update qty failed:", err); await loadCart(); }
    }, 600);
  }, [loadCart]);

  const toggleSelect = (id) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(String(id)) ? n.delete(String(id)) : n.add(String(id)); return n; });

  const allSelected =
  visibleCartItems.length > 0 &&
  visibleCartItems.every(i => selectedIds.has(String(i.id)));

  const selectedVisibleIds = useMemo(() => {
    return visibleCartItems
      .filter((item) => selectedIds.has(String(item.id)))
      .map((item) => String(item.id));
  }, [visibleCartItems, selectedIds]);
  const someSelected = selectedIds.size > 0;
  const toggleAll = () =>
  setSelectedIds(allSelected ? new Set() : new Set(visibleCartItems.map(i => String(i.id))));

  const { subtotal, currencySymbol } = useMemo(() => {
    const selectedItems = visibleCartItems.filter(i => selectedIds.has(String(i.id)));
    const firstItem = selectedItems[0];
    const symbol = firstItem ? getCurrencySymbol(firstItem) : "$";

    const total = selectedItems.reduce(
      (sum, item) => sum + resolveUnitPrice(item) * Number(item?.quantity ?? 1),
      0
    );

    return { subtotal: total, currencySymbol: symbol };
  }, [selectedIds, visibleCartItems]);

  return (
    <section className="cart py-80">
      <style>{CSS}</style>
      <div className="container container-lg">
        <div className="row gy-4">

          {/* LEFT */}
          <div className="col-xl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-8 px-24 px-sm-32 py-32 py-sm-40">

              {/* ✅ Location indicator */}
              {locationFilter && (
                <div className="cs-hint">
                  <i className="ph ph-map-pin" style={{ marginRight: 6 }}></i>
                  Showing cart items for <strong>{locationFilter}</strong>
                </div>
              )}

              {/* Empty */}
              {visibleCartItems.length === 0 ? (
                <div className="py-48 text-center text-gray-500">
                  <div style={{ fontSize: 48 }} className="mb-16">🛒</div>
                  <p className="mb-16 fw-semibold">
                    {locationFilter 
                      ? `No items in cart for ${locationFilter}` 
                      : "Your cart is empty"}
                  </p>
                  <Link to="/shop" className="btn btn-main py-12 px-32 rounded-8">Go to Shop</Link>
                </div>
              ) : (
                <>
                  {/* Toolbar */}
                  <div className="cs-toolbar">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <label className="d-flex align-items-center gap-2 fw-bold mb-0"
                        style={{ fontSize: 13, color: "#344054", cursor: "pointer", userSelect: "none" }}>
                        <input type="checkbox" className="cs-cb" checked={allSelected} onChange={toggleAll} />
                        {allSelected ? "Deselect All" : "Select All"}
                      </label>
                      {someSelected && (
                        <span className="cs-sel-badge">
                          {selectedVisibleIds.length} of {visibleCartItems.length} selected
                        </span>
                      )}
                    </div>
                    {selectedVisibleIds.length > 1 && (
                      <button className="cs-rem-sel" onClick={removeSelected}>
                        Remove Selected ({selectedVisibleIds.length})
                      </button>
                    )}
                  </div>

                  {/* Desktop Table */}
                  <div className="d-none d-md-block overflow-x-auto scroll-sm">
                    <table className="table style-three w-100">
                      <tbody>
                        {visibleCartItems.map((item) => {
                          const id = String(item.id), sel = selectedIds.has(id);
                          const unitPrice = resolveUnitPrice(item), qty = Number(item?.quantity ?? 1);
                          const isFlash = hasFlashSale(item);
                          const itemCurrency = getCurrencySymbol(item);
                          
                          return (
                            <tr key={id} className={`cs-trow${sel ? " cs-sel-row" : ""}`}>
                              <td><input type="checkbox" className="cs-cb" checked={sel} onChange={() => toggleSelect(id)} /></td>
                              <td>
                                <div className="d-flex align-items-center gap-16">
                                  <Link to={resolveDetailsUrl(item)} className="border border-gray-100 rounded-8 flex-center" style={{ width: 72, height: 72, flexShrink: 0 }}>
                                    <img src={resolveImage(item)} alt={resolveTitle(item)} className="cs-thumb" />
                                  </Link>
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <Link to={resolveDetailsUrl(item)} className="cs-ptitle d-block mb-4">
                                        {resolveTitle(item)}
                                      </Link>
                                      {isFlash && (
                                        <span className="cs-flash-badge">🔥 Flash Sale</span>
                                      )}
                                    </div>
                                    {resolveCategory(item) && <span className="cs-cat">{resolveCategory(item)}</span>}
                                  </div>
                                </div>
                              </td>
                              <td className={`cs-price${isFlash ? " cs-flash" : ""}`}>{money(unitPrice, itemCurrency)}</td>
                              <td><QuantityControl initialQuantity={qty} onChange={(q) => setQty(item.id, q)} /></td>
                              <td className={`cs-sub${isFlash ? " cs-flash" : ""}`}>{money(unitPrice * qty, itemCurrency)}</td>
                              <td>
                                <button className="cs-xbtn" onClick={() => removeItem(item.id)} title="Remove"><TrashIcon /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="d-md-none">
                    {visibleCartItems.map((item) => {
                      const id = String(item.id), sel = selectedIds.has(id);
                      const unitPrice = resolveUnitPrice(item), qty = Number(item?.quantity ?? 1);
                      const isFlash = hasFlashSale(item);
                      const itemCurrency = getCurrencySymbol(item);
                      
                      return (
                        <div key={id} className={`cs-mcard${sel ? " cs-sel-row" : ""}`}>
                          <div className="cs-m-top">
                            <input type="checkbox" className="cs-cb" style={{ marginTop: 4 }}
                              checked={sel} onChange={() => toggleSelect(id)} />
                            <Link to={resolveDetailsUrl(item)} style={{ flexShrink: 0 }}>
                              <img src={resolveImage(item)} alt={resolveTitle(item)} className="cs-mimg"
                                onError={e => { e.currentTarget.src = "/assets/images/thumbs/product-thumb-1.png"; }} />
                            </Link>
                            <div className="cs-m-info">
                              <div className="cs-m-title-row">
                                <div>
                                  <Link to={resolveDetailsUrl(item)} className="cs-ptitle d-block">
                                    {resolveTitle(item)}
                                  </Link>
                                  {isFlash && (
                                    <span className="cs-flash-badge" style={{ marginLeft: 0, marginTop: 4 }}>
                                      🔥 Flash Sale
                                    </span>
                                  )}
                                </div>
                                <button className="cs-xbtn" onClick={() => removeItem(item.id)} title="Remove">
                                  <TrashIcon />
                                </button>
                              </div>
                              {resolveCategory(item) && (
                                <span className="cs-cat">{resolveCategory(item)}</span>
                              )}
                            </div>
                          </div>
                          <div className="cs-m-bottom">
                            <div className="cs-m-pricebox">
                              <span className="cs-m-plabel">Unit Price</span>
                              <span className={`cs-m-pval${isFlash ? " cs-flash" : ""}`}>{money(unitPrice, itemCurrency)}</span>
                            </div>
                            <QuantityControl initialQuantity={qty} onChange={(q) => setQty(item.id, q)} />
                            <div className="cs-m-subbox">
                              <span className="cs-m-sublabel">Subtotal</span>
                              <span className={`cs-m-subval${isFlash ? " cs-flash" : ""}`}>{money(unitPrice * qty, itemCurrency)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-16 mt-24">
                    <div className="d-flex flex-wrap gap-12 flex-grow-1">
                      <input
                        type="text"
                        className="common-input flex-grow-1"
                        placeholder="Coupon Code"
                        value={coupon}
                        onChange={e => setCoupon(e.target.value)}
                        style={{ minWidth: 160 }}
                      />
                      <button type="button" className="btn btn-main py-18 rounded-8 flex-shrink-0">
                        Apply Coupon
                      </button>
                    </div>
                    <button type="button" className="text-lg text-gray-500 hover-text-main-600 bg-transparent border-0" onClick={loadCart}>
                      Update Cart
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="col-xl-3 col-lg-4">
            <div className="cart-sidebar border border-gray-100 rounded-8 px-20 px-sm-24 py-32 py-sm-40"
              style={{ position: "sticky", top: 20 }}>
              <h6 className="text-xl mb-24">Cart Totals</h6>

              {!someSelected && <p className="cs-hint">Select items to calculate total</p>}

              <div className="bg-color-three rounded-8 p-20 p-sm-24 mb-16">
                <div className="flex-between gap-8">
                  <span className="text-gray-900 font-heading-two">Subtotal</span>
                  <span className="text-gray-900 fw-semibold">
                    {money(subtotal, currencySymbol)}
                  </span>
                </div>
              </div>

              <div className="bg-color-three rounded-8 p-20 p-sm-24 flex-between gap-8 mb-0">
                <span className="text-gray-900 text-xl fw-semibold">Total</span>
                <span className="text-gray-900 text-xl fw-semibold">{money(subtotal, currencySymbol)}</span>
              </div>

              <button
                type="button"
                className={`btn btn-main mt-32 py-18 w-100 rounded-8${!someSelected ? " opacity-50" : ""}`}
                disabled={!someSelected}
                onClick={() => {
                  navigate("/checkout", {
                    state: { selectedIds: selectedVisibleIds },
                  });
                }}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
export default CartSection;